"use client";

import { appConfig, chainSlug } from "@/constants";
import type { Erc20Token } from "@/types";
import { notifyError } from "@/utils/monitoring";
import axios from "axios";
import {
  type PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePassportProvider } from "./PassportProvider";

export type ImportOrigin =
  | "CollectionsEmptyState"
  | "ActionMenuLinkWallet"
  | "ExternalWallets"
  | "ActionMenu"
  | "CollectionCard"
  | "ImportSuccess"
  | "ItemDetails";

export type ActiveAssetImport = {
  importAddress: string;
  trackingId: string;
};

export const Erc20Context = createContext<{
  tokens: Map<string, Erc20Token>;
  fetchToken: (address: string) => Erc20Token | undefined;
}>({
  tokens: new Map(),
  fetchToken: () => undefined,
});

const useErc20 = () => {
  const ctx = useContext(Erc20Context);
  if (!ctx) {
    throw new Error("useErc20 must be used within a Erc20Provider");
  }
  return ctx;
};

const baseUrl = `${appConfig.IMMUTABLE_BASE_URI}/v1/chains/${chainSlug}`;

const Erc20Provider = ({ children }: PropsWithChildren) => {
  const { walletAddress } = usePassportProvider();
  // const walletAddress = "0x9ac254faaca6e215252430ed5ae14fe1fb2004ac"; // IMX && ETH test address
  // const walletAddress = "0xfae7b0240947B9d8a0Cf4adEE411706a2360074E"; // ILV && USD && ETH test address
  // const walletAddress = "0x9C36A49CBb76df9039DC06fB5E2930b63412245B"; // GOG && ETH test address
  const [tokenMap, setTokenMap] = useState<Map<string, Erc20Token>>(new Map());

  useEffect(() => {
    const getTokens = async () => {
      try {
        const path = `${baseUrl}/tokens?verification_status=verified&page_size=200`;
        const response = await axios.get(path);
        const tokens: Erc20Token[] = response.data.result;
        setTokenMap(
          new Map(
            tokens.map((token) => [
              token.contract_address.toLowerCase(),
              token,
            ]),
          ),
        );
      } catch (e) {
        notifyError(e, "getTokens");
        console.error(e);
      }
    };
    getTokens();
  }, []);

  const fetchToken = useCallback(
    (address: string) => {
      if (tokenMap.has(address)) {
        return tokenMap.get(address);
      }

      const getSingleToken = async () => {
        try {
          const path = `${baseUrl}/tokens/${address}`;
          const response = await axios.get(path);
          const token: Erc20Token = response.data.result;
          // need to update the state in order for UI's to re-render
          const newMap = new Map(tokenMap);
          newMap.set(address, token);
          setTokenMap(newMap);
        } catch (e) {
          console.error(e);
        }
      };
      getSingleToken();

      return undefined;
    },
    [tokenMap],
  );

  return (
    <Erc20Context.Provider
      value={{
        tokens: tokenMap,
        fetchToken,
      }}
    >
      {children}
    </Erc20Context.Provider>
  );
};

export { Erc20Provider, useErc20 };
